import React from "react";
import { useSystemId } from "../../../hooks/useReduxSelectors";
import { sendCommand } from "../../../ducks/uiDuck";
import { useDispatch } from "react-redux";
import { setQal3RunManuallyBtnStatus } from "../../../ducks/settingsUIDuck";
import useRoutineStartStopButtonWithCounter from "../../../helpers/useRoutineStartStopButtonWithCounter";
import { Box, Stack, Select, MenuItem, TextField, Switch, FormControlLabel, Button, Typography, Table, TableHead, TableBody, TableRow, TableCell, CircularProgress } from "@mui/material";

const days = [
  "sunday",
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
];

const QAL3SettingsForm = ({ qal3Settings = { isQal3Start: false }, settingsUI = { qal3: { isRunManuallyBtnDisabled: false } }, onChangeHandler, inputSelected, t = () => { }, solution = null }) => {

  const systemId = useSystemId();
  const dispatch = useDispatch();
  const startButtonDisabled = settingsUI.qal3.isRunManuallyBtnDisabled;
  const resetButtonDisabled = settingsUI.qal3.isRunManuallyBtnDisabled;

  const qal3InUse = qal3Settings.inUse;

  const qal3RunManuallyButton = (buttonCommand) => {
    if (buttonCommand === "Start") {
      dispatch(setQal3RunManuallyBtnStatus({ systemId, data: { isDisabled: true } }));
      dispatch(sendCommand({
        argsProps: {
          HwId: "EdgeDevice",
          Command: "Qal3",
          Value: "Start",
        },
        commandType: "Qal3",
        systemId,
      }));
    }
  };

  const resetAlarmManually = () => {
    dispatch(
      sendCommand({
        argsProps: {
          HwId: "EdgeDevice",
          Command: "Qal3",
          Value: "Reset"
        },
        commandType: "Qal3",
        systemId
      })
    );
  };

  const { startCounter, stopCounter } = useRoutineStartStopButtonWithCounter(systemId, dispatch, sendCommand, "Qal3");

  return (
    <Box sx={{ p: 2 }}>
      {/* Top controls */}
      <Box sx={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", mb: 2 }}>
        <FormControlLabel
          control={<Switch
            checked={qal3Settings.inUse}
            onChange={() => onChangeHandler("inUse", !qal3Settings.inUse)}
          />}
          label={qal3Settings.inUse ? "QAL3 in Use" : "QAL3 OFF"}
        />
        <Button disabled={resetButtonDisabled} size="medium" onClick={() => resetAlarmManually()}>
          {t("Reset Alarms")}
        </Button>
      </Box>

      {/* Schedule row */}
      <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", alignItems: "center", mb: 3 }}>
        <Typography>Automated run on</Typography>
        <Select
          value={qal3Settings.day !== undefined ? qal3Settings.day : ""}
          onChange={(e) => onChangeHandler("day", e.target.value)}
          size="small"
          displayEmpty
          sx={{ minWidth: 130 }}
          renderValue={(selected) => selected !== "" ? days[selected].toUpperCase() + "'S" : "Select day"}
        >
          {days.map((day, idx) => (
            <MenuItem key={day} value={idx}>
              {day.charAt(0).toUpperCase() + day.slice(1)}
            </MenuItem>
          ))}
        </Select>
        <Typography>at</Typography>
        <TextField
          value={qal3Settings.startHour ?? 0}
          onChange={(e) => onChangeHandler("startHour", e.target.value)}
          onClick={(e) => inputSelected(e, "startHour")}
          size="small"
          sx={{ width: 60 }}
        />
        <Typography>:</Typography>
        <TextField
          value={qal3Settings.startMinute ?? 0}
          onChange={(e) => onChangeHandler("startMinute", e.target.value)}
          onClick={(e) => inputSelected(e, "startMinute")}
          size="small"
          sx={{ width: 60 }}
        />
        <Typography>every</Typography>
        <TextField
          value={qal3Settings.intervalWeeks ?? 0}
          onChange={(e) => onChangeHandler("intervalWeeks", e.target.value)}
          onClick={(e) => inputSelected(e, "intervalWeeks")}
          size="small"
          sx={{ width: 60 }}
        />
        <Typography>weeks</Typography>
      </Stack>

      {/* Component values table */}
      <Table size="small" sx={{ mb: 3 }}>
        <TableHead>
          <TableRow>
            <TableCell />
            {qal3Settings.components && qal3Settings.components.map((qal3Comp, idx) => (
              <TableCell key={`hdr_${idx}`} align="center">{qal3Comp.name}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow>
            <TableCell>Zero Cal</TableCell>
            {qal3Settings.components && qal3Settings.components.map((qal3Comp, idx) => (
              <TableCell key={`zc_${idx}`} align="center">
                <TextField
                  size="small"
                  value={qal3Comp.zeroCal ?? ""}
                  onChange={(e) => onChangeHandler("zeroCal", e.target.value, qal3Comp.name)}
                  onClick={(e) => inputSelected(e, qal3Comp.name + "|zeroCal")}
                  error={qal3Comp.zeroCal !== "" && qal3Comp.zeroCal != null && isNaN(Number(String(qal3Comp.zeroCal).replace(",", ".")))}
                  helperText={qal3Comp.zeroCal !== "" && qal3Comp.zeroCal != null && isNaN(Number(String(qal3Comp.zeroCal).replace(",", "."))) ? "Input a number" : ""}
                  sx={{ width: 90 }}
                />
              </TableCell>
            ))}
          </TableRow>
          <TableRow>
            <TableCell>Zero Fail</TableCell>
            {qal3Settings.components && qal3Settings.components.map((qal3Comp, idx) => (
              <TableCell key={`zf_${idx}`} align="center">
                <TextField
                  size="small"
                  value={qal3Comp.zeroFail ?? ""}
                  onChange={(e) => onChangeHandler("zeroFail", e.target.value, qal3Comp.name)}
                  onClick={(e) => inputSelected(e, qal3Comp.name + "|zeroFail")}
                  error={qal3Comp.zeroFail !== "" && qal3Comp.zeroFail != null && isNaN(Number(String(qal3Comp.zeroFail).replace(",", ".")))}
                  helperText={qal3Comp.zeroFail !== "" && qal3Comp.zeroFail != null && isNaN(Number(String(qal3Comp.zeroFail).replace(",", "."))) ? "Input a number" : ""}
                  sx={{ width: 90 }}
                />
              </TableCell>
            ))}
          </TableRow>
          <TableRow>
            <TableCell>Span Cal</TableCell>
            {qal3Settings.components && qal3Settings.components.map((qal3Comp, idx) => (
              <TableCell key={`sc_${idx}`} align="center">
                <TextField
                  size="small"
                  value={qal3Comp.spanCal ?? ""}
                  onChange={(e) => onChangeHandler("spanCal", e.target.value, qal3Comp.name)}
                  onClick={(e) => inputSelected(e, qal3Comp.name + "|spanCal")}
                  error={qal3Comp.spanCal !== "" && qal3Comp.spanCal != null && isNaN(Number(String(qal3Comp.spanCal).replace(",", ".")))}
                  helperText={qal3Comp.spanCal !== "" && qal3Comp.spanCal != null && isNaN(Number(String(qal3Comp.spanCal).replace(",", "."))) ? "Input a number" : ""}
                  sx={{ width: 90 }}
                />
              </TableCell>
            ))}
          </TableRow>
          <TableRow>
            <TableCell>Span Fail</TableCell>
            {qal3Settings.components && qal3Settings.components.map((qal3Comp, idx) => (
              <TableCell key={`sf_${idx}`} align="center">
                <TextField
                  size="small"
                  value={qal3Comp.spanFail ?? ""}
                  onChange={(e) => onChangeHandler("spanFail", e.target.value, qal3Comp.name)}
                  onClick={(e) => inputSelected(e, qal3Comp.name + "|spanFail")}
                  error={qal3Comp.spanFail !== "" && qal3Comp.spanFail != null && isNaN(Number(String(qal3Comp.spanFail).replace(",", ".")))}
                  helperText={qal3Comp.spanFail !== "" && qal3Comp.spanFail != null && isNaN(Number(String(qal3Comp.spanFail).replace(",", "."))) ? "Input a number" : ""}
                  sx={{ width: 90 }}
                />
              </TableCell>
            ))}
          </TableRow>
        </TableBody>
      </Table>

      {/* CEMS-TRS H2S/SO2 selection */}
      {solution === "CEMS-TRS" && (
        <Stack spacing={1} sx={{ alignItems: "center", mb: 2 }}>
          <Typography>TRS QAL 3 Using</Typography>
          <Stack direction="row" spacing={1}>
            <Button
              variant={qal3Settings.usingH2S ? "contained" : "outlined"}
              size="medium"
              onClick={() => onChangeHandler("usingH2S", true)}
            >
              H2S
            </Button>
            <Button
              variant={!qal3Settings.usingH2S ? "contained" : "outlined"}
              size="medium"
              onClick={() => onChangeHandler("usingH2S", false)}
            >
              SO2
            </Button>
          </Stack>
        </Stack>
      )}

      {/* Run manually */}
      <Stack direction="row" spacing={1} sx={{ justifyContent: "center", alignItems: "center" }}>
        <Button disabled={!qal3InUse || startButtonDisabled} size="medium" onClick={() => qal3RunManuallyButton("Start")}>
          {t("Start")}
        </Button>
        <Button
          disabled={!qal3InUse || !startButtonDisabled}
          size="medium"
          color="error"
          onMouseDown={startCounter}
          onMouseUp={stopCounter}
          onMouseLeave={stopCounter}
          onTouchStart={startCounter}
          onTouchEnd={stopCounter}
          onTouchCancel={stopCounter}>
          {t("Stop")}
        </Button>
      </Stack>
      {startButtonDisabled && (
        <Stack direction="row" spacing={1} sx={{ justifyContent: "center", alignItems: "center", mt: 1 }}>
          <Typography>QAL3 running</Typography>
          <CircularProgress size={16} sx={{ color: "common.white" }} />
        </Stack>
      )}
    </Box>
  );
};

export default QAL3SettingsForm;
